import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { formatPrice, formatDate } from '@/lib/rooms';

interface RoomItem {
  id: string;
  name: string;
  description: string;
  price_per_night: number;
  capacity: number;
  bed_type: string;
  images: string[];
  amenities: string[];
  available: boolean;
  status: string;
}

interface BookingItem {
  id: string;
  room_id: string;
  check_in: string;
  check_out: string;
  nights: number;
  guests: number;
  total_price: number;
  customer_name: string;
  customer_phone: string;
  status: string;
  payment_status: string;
  created_at: string;
}

type Tab = 'rooms' | 'bookings';

function RoomsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [rooms, setRooms] = useState<RoomItem[]>([]);
  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('rooms');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const { data: biz } = await supabase
          .from('businesses')
          .select('id')
          .eq('owner_id', user.id)
          .maybeSingle();
        if (!mounted) return;
        setBusinessId(biz?.id ?? null);

        if (biz?.id) {
          const [roomsRes, bookingsRes] = await Promise.all([
            supabase
              .from('hotel_rooms')
              .select('*')
              .eq('business_id', biz.id)
              .order('created_at', { ascending: false }),
            supabase
              .from('bookings')
              .select('*')
              .eq('business_id', biz.id)
              .order('created_at', { ascending: false }),
          ]);
          if (mounted) {
            setRooms((roomsRes.data as RoomItem[]) || []);
            setBookings((bookingsRes.data as BookingItem[]) || []);
          }
        }
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const roomName = (roomId: string) => rooms.find((r) => r.id === roomId)?.name ?? 'Chambre supprimée';

  const handleDeleteRoom = async (id: string) => {
    if (!window.confirm('Supprimer cette chambre ? Cette action est irréversible.')) return;
    const { error: delErr } = await supabase.from('hotel_rooms').delete().eq('id', id);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setRooms((prev) => prev.filter((r) => r.id !== id));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Mes chambres & réservations</h1>
          <p className="text-sm text-foreground-400 mt-1">
            Gérez les chambres de votre hôtel et suivez les réservations reçues.
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/rooms/new')}
          className="px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line" />
          Ajouter une chambre
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-6">{error}</div>
      )}

      {/* Tabs */}
      <div className="inline-flex items-center bg-background-100 border border-background-200/70 rounded-full p-1 mb-6">
        <button
          type="button"
          onClick={() => setTab('rooms')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
            tab === 'rooms' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-400 hover:text-foreground-50'
          }`}
        >
          Chambres ({rooms.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('bookings')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
            tab === 'bookings' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-400 hover:text-foreground-50'
          }`}
        >
          Réservations ({bookings.length})
        </button>
      </div>

      {!businessId ? (
        <div className="text-center py-16 border border-dashed border-background-300/60 rounded-xl">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-store-2-line text-2xl text-foreground-400" />
          </div>
          <p className="text-foreground-500 font-medium mb-1">Aucune entreprise enregistrée</p>
          <p className="text-sm text-foreground-500 mb-6">
            Vous devez d&apos;abord inscrire votre hôtel pour gérer vos chambres.
          </p>
          <Link
            to="/auth/register"
            className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Inscrire mon entreprise
          </Link>
        </div>
      ) : tab === 'rooms' ? (
        rooms.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-background-300/60 rounded-xl">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-hotel-bed-line text-2xl text-foreground-400" />
            </div>
            <p className="text-foreground-500 font-medium mb-1">Aucune chambre pour le moment</p>
            <p className="text-sm text-foreground-500 mb-6">Ajoutez votre première chambre pour commencer à recevoir des réservations.</p>
            <button
              onClick={() => navigate('/dashboard/rooms/new')}
              className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
            >
              Ajouter ma première chambre
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rooms.map((room) => (
              <div key={room.id} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-28 h-24 shrink-0 rounded-lg overflow-hidden bg-background-200/60">
                  {room.images && room.images.length > 0 ? (
                    <img src={room.images[0]} alt={room.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-2xl text-foreground-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h2 className="font-heading font-semibold text-foreground-50 truncate">{room.name}</h2>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        room.available ? 'bg-accent-500/15 text-accent-400' : 'bg-background-200/60 text-foreground-500'
                      }`}
                    >
                      {room.available ? 'Disponible' : 'Indisponible'}
                    </span>
                  </div>
                  <p className="text-sm text-foreground-400 mb-2">
                    {room.capacity} pers. · {room.bed_type}
                  </p>
                  <span className="font-heading font-bold text-primary-400">{formatPrice(room.price_per_night)} / nuit</span>
                  <div className="flex gap-2 mt-3">
                    <Link
                      to={`/dashboard/rooms/${room.id}/edit`}
                      className="px-3 py-1.5 text-xs font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors whitespace-nowrap"
                    >
                      Modifier
                    </Link>
                    <button
                      onClick={() => handleDeleteRoom(room.id)}
                      className="px-3 py-1.5 text-xs font-medium text-red-400 border border-red-400/30 rounded-md hover:bg-red-500/10 transition-colors whitespace-nowrap"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : bookings.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-background-300/60 rounded-xl">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-calendar-check-line text-2xl text-foreground-400" />
          </div>
          <p className="text-foreground-500 font-medium mb-1">Aucune réservation pour le moment</p>
          <p className="text-sm text-foreground-500">Les réservations de vos clients apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10">
                    <i className="ri-calendar-check-line text-lg text-primary-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground-50 truncate">
                      {b.customer_name || 'Client'} · {roomName(b.room_id)}
                    </p>
                    <p className="text-xs text-foreground-400">
                      {formatDate(b.check_in)} → {formatDate(b.check_out)} · {b.nights} nuit{b.nights > 1 ? 's' : ''} · {b.guests} pers.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-heading font-bold text-primary-400">{formatPrice(b.total_price)}</span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-accent-500/15 text-accent-400">
                    {b.status === 'confirmed' ? 'Confirmée' : b.status}
                  </span>
                </div>
              </div>
              {b.customer_phone && (
                <p className="text-xs text-foreground-400 mt-2 flex items-center gap-1.5">
                  <i className="ri-phone-line" />
                  {b.customer_phone}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

export default RoomsDashboard;